import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useAIAssistant } from '@shared/contexts/AIAssistantContext';
import createQueryConfig, { keys } from '../../queries';
import useMerchantSettings from '../../hooks/use-merchant-settings';
import { FetchMerchantOnboardingsV2Response } from '@shared/types';
import { usePostMessageChannel } from './usePostMessageChannel';
import { changeLocation } from '../../actions';
import { useAppDispatch } from './use-app-dispatch';
import EVENT_BUS_EVENTS from '../../constants/event-bus-events';

// postMessage event type emitted by micro-apps to request navigation to a
// given path within app-shell (see chat_components.ex hint/1 + app.js GoToPage hook).
export const ONBOARDING_NAVIGATE_TO_SCREEN = 'ONBOARDING_NAVIGATE_TO_SCREEN';

// eventBus events that should trigger a refetch of the onboarding steps.
export const EVENT_BUS_ONBOARDING_EVENTS_TO_LISTEN_FOR = [
  'company_settings_updated',
  'service_created',
  'service_updated',
  'appointment_created',
];

// postMessage event types emitted by micro-apps that should trigger a refetch
// of the onboarding steps (mirrors the eventBus events below).
export const POST_MESSAGE_ONBOARDING_EVENTS_TO_LISTEN_FOR = [
  'ONBOARDING_CREATE_SERVICE_COMPLETED',
  'ONBOARDING_SETUP_ADDRESS_COMPLETED',
  'ONBOARDING_CREATE_APPOINTMENT_COMPLETED',
  'ONBOARDING_ACTIVATE_SUBSCRIPTION_COMPLETED',
];

export function useFetchOnboardingSteps() {
  const { queryClient, apiClient, eventBus } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { closeAIAssistant } = useAIAssistant();

  const { merchantSettings } = useMerchantSettings();
  const { merchantId, locale } = merchantSettings || {};

  const queryResult = useQuery<FetchMerchantOnboardingsV2Response>({
    ...queryConfig.merchantOnboardingsV2.fetchOnboardingSteps({
      merchantId,
      locale,
    }),
    enabled: Boolean(merchantId),
    retry: false,
  });

  // Create a debounced invalidation function
  const invalidateOnboardingQuery = useCallback(() => {
    if (!queryClient || !merchantId) return;

    const currentData = queryResult.data;

    if (!currentData) {
      const token =
        queryClient.getQueryData(['merchant_account_token']) || 'loading';

      const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
        merchantId,
        String(token)
      );
      queryClient.invalidateQueries({ queryKey });
      return;
    }

    // Check if onboarding is completed
    const isCompleted =
      currentData?.overall_progress_percentage === 100 ||
      currentData?.completed_at !== null;

    if (!isCompleted) {
      const token =
        queryClient.getQueryData(['merchant_account_token']) || 'loading';

      const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
        merchantId,
        String(token)
      );
      queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, merchantId, queryResult.data]);

  // Debounce the invalidation with 1 second delay
  const debouncedInvalidateQuery = useMemo(
    () => debounce(invalidateOnboardingQuery, 1000),
    [invalidateOnboardingQuery]
  );

  // Listen for postMessage events emitted by micro-apps and refetch
  // onboarding data when a relevant onboarding step is completed.
  const handlePostMessage = useCallback(
    ({ message }: { message: { type: string; path?: string } }) => {
      if (POST_MESSAGE_ONBOARDING_EVENTS_TO_LISTEN_FOR.includes(message.type)) {
        debouncedInvalidateQuery();
      }
      if (
        message.type === ONBOARDING_NAVIGATE_TO_SCREEN &&
        typeof message.path === 'string' &&
        message.path.length > 0
      ) {
        eventBus.emit(EVENT_BUS_EVENTS.CLOSE_ONBOARDING_PROGRESS_MODAL);
        closeAIAssistant();
        navigate(message.path);
        dispatch(changeLocation(message.path));
      }
    },
    [debouncedInvalidateQuery, navigate, dispatch, eventBus, closeAIAssistant]
  );

  usePostMessageChannel({ onMessage: handlePostMessage });

  // Set up event bus listeners to refetch onboarding data conditionally
  useEffect(() => {
    if (!eventBus || !queryClient || !merchantId) return;

    const handleConditionalRefetch = () => {
      // Use debounced invalidation
      debouncedInvalidateQuery();
    };

    // Register listeners
    EVENT_BUS_ONBOARDING_EVENTS_TO_LISTEN_FOR.forEach((event) => {
      eventBus.on(event, handleConditionalRefetch);
    });

    // Cleanup listeners and cancel debounce on unmount
    return () => {
      EVENT_BUS_ONBOARDING_EVENTS_TO_LISTEN_FOR.forEach((event) => {
        eventBus.off(event, handleConditionalRefetch);
      });

      // Cancel any pending debounced calls
      debouncedInvalidateQuery.cancel();
    };
  }, [eventBus, queryClient, merchantId, debouncedInvalidateQuery]);

  return queryResult;
}

export function useSkipOnboardingStep() {
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const { merchantId } = merchantSettings || {};

  // Create debounced invalidation for mutations (shorter delay)
  const debouncedMutationInvalidate = useMemo(
    () =>
      debounce(() => {
        const token =
          queryClient.getQueryData(['merchant_account_token']) || 'loading';

        const queryKey = keys.merchantOnboardingsV2.fetchOnboardingSteps(
          merchantId,
          String(token)
        );

        queryClient.invalidateQueries({ queryKey });
      }, 1000),
    [queryClient, merchantId]
  );

  return useMutation({
    ...queryConfig.merchantOnboardingsV2.skipOnboardingStep({ merchantId }),
    onSuccess: () => {
      // Use debounced invalidation with 1 second delay
      debouncedMutationInvalidate();
    },
  });
}
